import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  updateDoc,
  query,
  where,
  limit,
} from "firebase/firestore";
import { User } from "firebase/auth";
import { db, storage } from "../configs/firebase";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";

const USERS_COLLECTION = "users";

const normalizeEmail = (value?: string | null) =>
  String(value || "")
    .trim()
    .toLowerCase();

const normalizePhone = (value?: string | null) => {
  const raw = String(value || "")
    .replace(/[^\d+]/g, "")
    .trim();
  if (!raw) return "";
  if (raw.startsWith("+")) return raw.slice(1);
  return raw;
};

const findUserByEmail = async (email: string) => {
  const normalizedEmail = normalizeEmail(email);
  if (!normalizedEmail) return null;

  const q = query(
    collection(db, USERS_COLLECTION),
    where("email", "==", normalizedEmail),
    limit(1),
  );

  const snap = await getDocs(q);
  if (snap.empty) return null;

  return {
    id: snap.docs[0].id,
    ...snap.docs[0].data(),
  };
};

const findUserByPhone = async (phoneNumber: string) => {
  const normalizedPhone = normalizePhone(phoneNumber);
  if (!normalizedPhone) return null;

  const q1 = query(
    collection(db, USERS_COLLECTION),
    where("phoneNumber", "==", normalizedPhone),
    limit(1),
  );

  const snap1 = await getDocs(q1);
  if (!snap1.empty) {
    return {
      id: snap1.docs[0].id,
      ...snap1.docs[0].data(),
    };
  }

  const q2 = query(
    collection(db, USERS_COLLECTION),
    where("phone", "==", normalizedPhone),
    limit(1),
  );

  const snap2 = await getDocs(q2);
  if (!snap2.empty) {
    return {
      id: snap2.docs[0].id,
      ...snap2.docs[0].data(),
    };
  }

  return null;
};

export const createUserProfile = async (
  user: User,
  options?: {
    displayName?: string;
  },
) => {
  const userRef = doc(db, USERS_COLLECTION, user.uid);
  const snap = await getDoc(userRef);

  const normalizedEmail = normalizeEmail(user.email);
  const displayName = String(
    options?.displayName || user.displayName || "",
  ).trim();

  if (!snap.exists()) {
    await setDoc(userRef, {
      id: user.uid,
      name: displayName || "",
      displayName: displayName || "",
      email: normalizedEmail || "",
      role: "guest",
      photoURL: user.photoURL || "",
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    });

    return {
      id: user.uid,
      created: true,
    };
  }

  const existingData = snap.data() || {};
  const patch: Record<string, any> = {
    updatedAt: new Date().toISOString(),
  };

  if (!existingData.name && displayName) {
    patch.name = displayName;
  }

  if (!existingData.displayName && displayName) {
    patch.displayName = displayName;
  }

  if (!existingData.email && normalizedEmail) {
    patch.email = normalizedEmail;
  }

  if (!existingData.photoURL && user.photoURL) {
    patch.photoURL = user.photoURL;
  }

  if (!existingData.role) {
    patch.role = "guest";
  }

  if (Object.keys(patch).length > 1) {
    await updateDoc(userRef, patch);
  }

  return {
    id: user.uid,
    created: false,
  };
};

type ClaimPhoneError = Error & {
  code?: string;
  status?: number;
};

export const claimPhoneNumberViaBackend = async (params: {
  currentUid: string;
  phoneNumber: string;
}) => {
  const apiBase = process.env.REACT_APP_BACKEND_URL || "";

  const response = await fetch(`${apiBase}/api/v1/users/claim-phone`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      currentUid: params.currentUid,
      phoneNumber: params.phoneNumber,
    }),
  });

  const data = await response.json().catch(() => ({}));

  if (!response.ok || !data?.ok) {
    const err: ClaimPhoneError = new Error(
      data?.message || "Failed to save phone number.",
    );
    err.code = data?.code || "CLAIM_PHONE_FAILED";
    err.status = response.status;
    throw err;
  }

  return data;
};

export const checkPhoneAvailabilityViaBackend = async (params: {
  currentUid: string;
  phoneNumber: string;
}) => {
  const apiBase = process.env.REACT_APP_BACKEND_URL || "";
  const phoneNumber = encodeURIComponent(params.phoneNumber);
  const currentUid = encodeURIComponent(params.currentUid);

  const response = await fetch(
    `${apiBase}/api/v1/users/phone-availability?currentUid=${currentUid}&phoneNumber=${phoneNumber}`,
  );

  const data = await response.json().catch(() => ({}));

  if (!response.ok || !data?.ok) {
    const err: ClaimPhoneError = new Error(
      data?.message || "Failed to check phone number.",
    );
    err.code = data?.code || "PHONE_AVAILABILITY_CHECK_FAILED";
    err.status = response.status;
    throw err;
  }

  return data as {
    ok: true;
    available: boolean;
    normalizedPhone: string;
    ownerUid: string | null;
  };
};

export const fetchUsersFromFirestore = async () => {
  const usersSnapshot = await getDocs(collection(db, USERS_COLLECTION));
  return usersSnapshot.docs.map((docSnap) => ({
    id: docSnap.id,
    ...docSnap.data(),
  }));
};

export const uploadLogo = async (file: File, uid: string) => {
  const fileRef = ref(storage, `logos/${uid}`);
  await uploadBytes(fileRef, file);
  return await getDownloadURL(fileRef);
};

export const updateUserSettings = async (uid: string, data: any) => {
  const userRef = doc(db, USERS_COLLECTION, uid);

  const nextPhone = normalizePhone(data?.phoneNumber || data?.phone);
  const nextEmail = normalizeEmail(data?.email);

  if (nextPhone) {
    const existingByPhone = await findUserByPhone(nextPhone);
    if (existingByPhone && existingByPhone.id !== uid) {
      throw new Error(
        "This phone number is already registered to another account.",
      );
    }
  }

  if (nextEmail) {
    const existingByEmail = await findUserByEmail(nextEmail);
    if (existingByEmail && existingByEmail.id !== uid) {
      throw new Error(
        "This email address is already registered to another account.",
      );
    }
  }

  await updateDoc(userRef, {
    ...data,
    ...(nextEmail ? { email: nextEmail } : {}),
    updatedAt: new Date().toISOString(),
  });
};

export const validateUserRegistrationAvailability = async (params: {
  email?: string;
  phoneNumber?: string;
  currentUid?: string;
}) => {
  const normalizedEmail = normalizeEmail(params.email);
  const normalizedPhone = normalizePhone(params.phoneNumber);
  const currentUid = String(params.currentUid || "").trim();

  if (normalizedEmail) {
    const existingByEmail = await findUserByEmail(normalizedEmail);
    if (existingByEmail && existingByEmail.id !== currentUid) {
      throw new Error(
        "This email address is already registered. Please sign in instead.",
      );
    }
  }

  if (normalizedPhone) {
    const existingByPhone = await findUserByPhone(normalizedPhone);
    if (existingByPhone && existingByPhone.id !== currentUid) {
      throw new Error(
        "This phone number is already registered. Please use the existing account or a different number.",
      );
    }
  }

  return true;
};
